<template>
  <label
    class="rounded-md border p-2 py-1.5 flex items-center justify-between gap-3 cursor-pointer hover:border-primary duration-300"
    :class="
      integrationId === userIntegration.id ? 'border-primary' : 'dark:border-surface-700'
    "
  >
    <span
      class="grid shrink-0 place-content-center bg-primary text-surface-0 h-8 w-8 sm:h-9 sm:w-9 rounded-md"
    >
      <svg
        class="shrink-0 fill-current"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        height="24"
        width="24"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 22v-5M9 8V2m6 6V2m3 6v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z"
        />
      </svg>
    </span>
    <div class="grow">
      <p class="font-medium leading-snug">
        {{ displayName }}
      </p>
      <div class="grid items-center grid-cols-[auto_1fr] gap-1.5">
        <span
          class="bg-surface-200 grid h-5 w-5 place-content-center rounded-full overflow-hidden dark:bg-surface-700"
        >
          <i class="pi pi-user text-xs"></i>
        </span>
        <p class="text-sm opacity-80 truncate">{{ activeAccountName }}</p>
      </div>
    </div>

    <input
      v-model="integrationId"
      type="radio"
      class="form-radio rounded-full duration-300"
      :value="userIntegration.id"
      name="integration"
    />
  </label>
</template>

<script setup lang="ts">
import type {
  IFacebookData,
  ILinkedInData,
  IEmailIntegrationData,
  IEmailMarketingData,
  ITwitterData,
  IWordpressData,
  IntegrationTypes,
  ITeamsData,
  ITikTokData,
  IUserIntegration,
  IZapierIntegrationData,
} from "@/types/account";

const props = defineProps<{
  platform: IntegrationTypes;
  userIntegration: IUserIntegration<IntegrationTypes>;
}>();

const integrationId = defineModel<string>();

const displayName = computed(() => {
  if (props.platform === "tiktok") {
    const tiktokData = props.userIntegration.data as ITikTokData;
    return (
      tiktokData.profile?.creator_nickname ||
      props.userIntegration.name ||
      props.userIntegration.id
    );
  }
  return props.userIntegration.name || props.userIntegration.id || props.platform;
});

const activeAccountName = computed(() => {
  if (!props.userIntegration?.data) return "n/a";
  switch (props.platform) {
    case "tiktok": {
      const tiktokData = props.userIntegration.data as ITikTokData;
      return tiktokData.profile?.creator_username
        ? `@${tiktokData.profile.creator_username}`
        : "n/a";
    }
    case "twitter": {
      const twitterData = props.userIntegration.data as ITwitterData;
      return twitterData.profile?.name || "n/a";
    }
    case "instagram":
    case "facebook": {
      const fbData = props.userIntegration.data as IFacebookData;
      return fbData.pages?.find(p => p.pageId === fbData.pageId)?.name || "n/a";
    }
    case "linkedin": {
      const linkedinData = props.userIntegration.data as ILinkedInData;
      return linkedinData.profile?.name || "n/a";
    }
    case "wordpress": {
      const wpData = props.userIntegration.data as IWordpressData;
      return wpData.profile?.username || "n/a";
    }
    case "email-marketing": {
      const emailMarketingData = props.userIntegration.data as IEmailMarketingData;
      return emailMarketingData.emailAddress || "n/a";
    }
    case "email": {
      const emailData = props.userIntegration.data as IEmailIntegrationData;
      return emailData.targetEmail || emailData.emailAddress || "n/a";
    }
    case "zapier":
      return (
        ((props.userIntegration.data as IZapierIntegrationData).webHooks?.length || 0) +
        " webhooks"
      );
    case "teams":
      return (props.userIntegration.data as ITeamsData).outgoingUrl || "n/a";
    default:
      return "n/a";
  }
});
</script>
